/**
 * Plugin GLTFLoader : décode les images embarquées dans le GLB (bufferView) avec
 * `createImageBitmap(Blob)`, sans URL `blob:`.
 *
 * Le chargeur three.js crée une URL `blob:` par image puis la lit par `fetch` ou `<img>` ; la CSP
 * d'inside (connect-src et img-src sans `blob:`) le refuse et le bâtiment s'affiche sans couleur.
 * Décoder le Blob directement ne passe par aucune URL, donc par aucune directive CSP.
 * Les images référencées par URI restent au chargeur standard.
 */

/** Ce que le plugin lit de three.js (injecté : three est chargé à la demande). */
export interface TexturesThree {
  Texture: new (image: never) => object;
  NearestFilter: unknown;
  LinearFilter: unknown;
  NearestMipmapNearestFilter: unknown;
  LinearMipmapNearestFilter: unknown;
  NearestMipmapLinearFilter: unknown;
  LinearMipmapLinearFilter: unknown;
  ClampToEdgeWrapping: unknown;
  MirroredRepeatWrapping: unknown;
  RepeatWrapping: unknown;
}

/** Ce que le plugin lit du parseur GLTF. */
export interface TexturesParser {
  json: {
    textures?: { source?: number; sampler?: number; name?: string }[];
    images?: { bufferView?: number; mimeType?: string; uri?: string; name?: string }[];
    samplers?: { magFilter?: number; minFilter?: number; wrapS?: number; wrapT?: number }[];
  };
  associations: Map<unknown, unknown>;
  getDependency(type: 'bufferView', index: number): Promise<ArrayBuffer>;
}

type Decode = (blob: Blob) => Promise<unknown>;

const defaultDecode: Decode = blob => createImageBitmap(blob, { imageOrientation: 'none', premultiplyAlpha: 'none' });

export function embeddedTexturesPlugin(T: TexturesThree, decode: Decode = defaultDecode) {
  const filters: Record<number, unknown> = {
    9728: T.NearestFilter,
    9729: T.LinearFilter,
    9984: T.NearestMipmapNearestFilter,
    9985: T.LinearMipmapNearestFilter,
    9986: T.NearestMipmapLinearFilter,
    9987: T.LinearMipmapLinearFilter,
  };
  const wrappings: Record<number, unknown> = {
    33071: T.ClampToEdgeWrapping,
    33648: T.MirroredRepeatWrapping,
    10497: T.RepeatWrapping,
  };

  return (parser: TexturesParser) => ({
    name: 'wheere_embedded_textures',
    loadTexture(textureIndex: number): Promise<object> | null {
      const json = parser.json;
      const def = json.textures?.[textureIndex];
      const image = def?.source === undefined ? undefined : json.images?.[def.source];
      if (!def || !image || image.bufferView === undefined) {
        return null;
      }
      return parser
        .getDependency('bufferView', image.bufferView)
        .then(bytes => decode(new Blob([bytes], { type: image.mimeType })))
        .then(bitmap => {
          // Mêmes réglages que GLTFParser.loadTextureImage (three r160).
          const texture = new T.Texture(bitmap as never) as Record<string, unknown>;
          const sampler = (def.sampler === undefined ? undefined : json.samplers?.[def.sampler]) ?? {};
          texture['flipY'] = false;
          texture['needsUpdate'] = true;
          texture['name'] = def.name || image.name || '';
          texture['magFilter'] = filters[sampler.magFilter ?? -1] ?? T.LinearFilter;
          texture['minFilter'] = filters[sampler.minFilter ?? -1] ?? T.LinearMipmapLinearFilter;
          texture['wrapS'] = wrappings[sampler.wrapS ?? -1] ?? T.RepeatWrapping;
          texture['wrapT'] = wrappings[sampler.wrapT ?? -1] ?? T.RepeatWrapping;
          parser.associations.set(texture, { textures: textureIndex });
          return texture;
        });
    },
  });
}
